import {
  BulkProductRead,
  ProductTypeEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import formatUSD from "@/lib/format-currency";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
  loadDataTableSearch,
} from "@/components/ui/param-data-table";
import { Button } from "@/components/ui/button";
import { Plus, Archive, FlaskConical } from "lucide-react";
import {
  useGetBulkProducts,
  getGetBulkProductsQueryOptions,
} from "@/requests/gen/react-query/bulk-product";
import { RequirePermission } from "@/components/universal/require-permission";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { resolveProductImage } from "@/lib/default-image";
import { Header } from "@/components/universal/header";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useState } from "react";
import { Toggle } from "@/components/ui/toggle";
import { useQueryStates, type inferParserType } from "nuqs";

type BulkProductSearch = inferParserType<typeof dataTableSearchParsers>;

function bulkProductQueryParams(
  p: BulkProductSearch,
): Parameters<typeof getGetBulkProductsQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    is_deleted: p.is_deleted,
    product_types: p.product_types || [],
    entity_ids: p.entity_ids,
    only_samples: p.only_samples,
  };
}

export const Route = createFileRoute(
  "/_private/_practice-360/bulk-product/lookup",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBulkProductsQueryOptions(
        bulkProductQueryParams(loadDataTableSearch(location.searchStr)),
      ),
    ),
  component: BulkProductLookup,
});

// Define the columns for the table
interface BulkProductTableProps {
  link?: LinkProps;
  image: string;
  name: string;
  product_type: ProductTypeEnum;
  price: number;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<BulkProductTableProps>[] = [
  {
    header: "",
    accessorKey: "image",
    cell: ({ row }) => (
      <img
        src={row.original.image}
        alt="Product"
        className="h-12 w-12 rounded-sm object-contain"
      />
    ),
  },
  { header: "Name", accessorKey: "name" },
  { header: "Type", accessorKey: "product_type" },
  { header: "NDC Code", accessorKey: "ndc_code" },
  {
    header: "Price",
    accessorKey: "price",
    cell: (value) => {
      return formatUSD(value.getValue() as number);
    },
  },
  {
    header: "Entity",
    accessorKey: "entity",
  },
];

// Main component
function BulkProductLookup() {
  const [queryStates, setQueryStates] = useQueryStates(dataTableSearchParsers);

  const bulkProducts = useGetBulkProducts(bulkProductQueryParams(queryStates), {
    query: { placeholderData: keepPreviousData },
  });

  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: queryStates.entity_ids,
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const tableData: BulkProductTableProps[] | undefined =
    bulkProducts.data?.items.map((product: BulkProductRead) => {
      return {
        link: linkOptions({
          to: "/bulk-product/$bulkProductId",
          params: { bulkProductId: String(product.bulk_product_id) },
        }),
        image: resolveProductImage(product.catalog_product),
        name: `${product.catalog_product.is_sample ? "SAMPLE - " : ""}${product.display_name}`,
        product_type: product.catalog_product.product_base.product_type,
        ndc_code: product.catalog_product.ndc_code,
        price: product.entity_price || 0,
        entity: product.entity.name,
      };
    });

  return (
    <div>
      <title>Bulk Products - Legrande Health Portal</title>
      <Header
        crumbs={[
          { name: "Bulk Products", link: { to: "/bulk-product/lookup" } },
        ]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Bulk Products</CardTitle>
          <CardDescription>
            View your products below. You can search, filter, and click to see
            more details.
          </CardDescription>
          <CardAction>
            <RequirePermission permission="administrate_legrande">
              <Button variant="default" size="sm" asChild>
                <Link to="/bulk-product/create">
                  <Plus />
                  Add Bulk Product
                </Link>
              </Button>
            </RequirePermission>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={bulkProducts.isFetching} />
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(ProductTypeEnum).map((product_type) => {
                    return { label: product_type, value: product_type };
                  }),
                },
              ]}
              values={queryStates.product_types || []}
              onChange={(values) => setQueryStates({ product_types: values })}
            />
            <DropdownFilter
              title="Entity"
              options={[
                {
                  items:
                    entities.data?.pages
                      .flatMap((page) => page.items)
                      .map((entity) => {
                        return {
                          label: entity.name,
                          value: entity.entity_id,
                        };
                      }) ?? [],
                },
              ]}
              search={entitySearch}
              setSearch={setEntitySearch}
              isFetching={entities.isFetchingNextPage}
              fetchNextPage={entities.fetchNextPage}
              values={queryStates.entity_ids}
              onChange={(values) => setQueryStates({ entity_ids: values })}
            />
            <Toggle
              pressed={queryStates.is_deleted || false}
              onPressedChange={(pressed) => {
                setQueryStates({
                  is_deleted: pressed,
                });
              }}
            >
              <Archive />
              Archived
            </Toggle>
            <Toggle
              pressed={queryStates.only_samples}
              onPressedChange={(pressed) =>
                setQueryStates({ only_samples: pressed })
              }
            >
              <FlaskConical />
              Samples
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={bulkProducts.isLoading}
            isError={bulkProducts.isError}
            count={bulkProducts.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
